<template>
  <li
    v-if="fieldGroup.type && shouldDisplayFieldGroup"
    class="field-group"
    :data-anchor-id="fieldGroup.anchorId"
  >
    <div
      v-if="fieldGroup.type === 'structure-edit'"
      class="field-group-control"
    >
      <StructureEdit
        :structure-config="fieldGroup.structureConfig"
        :children-count="parserValues[tabItem.label].childrenStructureCount"
      />
    </div>
    <div
      v-else-if="fieldGroup.type === 'tab-item'"
      class="tabs-field-group"
    >
      <div
        v-if="fieldGroup.isNotCollapsible"
      >
        <span v-if="fieldGroup.label !== ''">{{ fieldGroupLabel }}</span>
      </div>
      <button
        v-else
        class="field-group-control field-group-control--collapsible flex justify-content-between align-items-center"
        @click="toggleFieldGroup(fieldGroupIndex)"
      >
        <span>{{ fieldGroupLabel }}</span>
        <i
          class="far"
          :class="activeFieldGroupIndex.includes(fieldGroupIndex) ? 'fa-chevron-up' : 'fa-chevron-down'"
        />
      </button>
      <div
        v-if="activeFieldGroupIndex.includes(fieldGroupIndex) || fieldGroup.isNotCollapsible"
        class="field-group-content field-group-content-tabbed"
      >
        <div
          v-if="isMobileEditableTabItem"
          class="mobile-edit-switch"
        >
          <div class="mobile-edit-switch__toggle">
            <button
              class="mobile-edit-switch__btn"
              :class="{ 'mobile-edit-switch__btn--active': !mobileEditMode }"
              @click="switchToDesktop"
            >
              <i class="far fa-desktop" />
              {{ t('templateBuilder.desktopMode') }}
            </button>
            <button
              v-tooltip.bottom="isMobileStylesDisabled ? t('templateBuilder.fields.warningMessages.disabledMobileDesign') : ''"
              class="mobile-edit-switch__btn"
              :class="{
                'mobile-edit-switch__btn--active': mobileEditMode,
                'mobile-edit-switch__btn--disabled': isMobileStylesDisabled,
              }"
              :disabled="isMobileStylesDisabled"
              @click="switchToMobile"
            >
              <i class="far fa-mobile" />
              {{ t('templateBuilder.mobileMode') }}
            </button>
          </div>
          <div
            v-if="mobileEditMode"
            class="mobile-edit-switch__indicator"
          >
            {{ t('templateBuilder.mobileEditModeIndicator') }}
          </div>
        </div>
        <div
          v-if="fieldGroup.canSave && fieldGroup.elementPartType"
          class="flex justify-content-end gap-2 mt-2"
        >
          <SpmButton
            v-if="!selectedSyncElement && componentInSyncPart"
            v-tooltip="t(`templateBuilder.structureActions.${fieldGroup.elementPartType}.edit`)"
            class="p-button p-button-secondary"
            icon="far fa-pen-to-square"
            @click="enterEditSyncElement"
          />
          <SpmButton
            v-if="!hideLoadElementBtn"
            v-tooltip="t(`templateBuilder.structureActions.${fieldGroup.elementPartType}.load`)"
            class="p-button p-button-secondary"
            icon="far fa-upload"
            @click="handleShowElementModal"
          />
          <SpmButton
            v-if="!componentInSyncPart && canSaveStyle"
            v-tooltip="t(`templateBuilder.structureActions.${fieldGroup.elementPartType}.save`)"
            class="p-button p-button-secondary"
            icon="far fa-star"
            @click="handleShowSaveElementModal"
          />
          <HelpMessage
            v-if="!hideLoadElementBtn"
            v-tooltip="t('showHelpArticle')"
            class="flex align-items-center"
            :link="{
              type: 'gleap',
              articleId: '166',
              label: ''
            }"
          />
        </div>
        <TabView
          ref="tabViewRef"
          :key="`tabs_${fieldGroup.id}`"
          :multiple="true"
          :scrollable="Object.prototype.hasOwnProperty.call(fieldGroup, 'scrollable') && fieldGroup.scrollable"
          @tab-change="(event) => handleSubTabChange(event, fieldGroup.tabPanel.items)"
        >
          <TabPanel
            v-for="(subTabItem, subIndex) of fieldGroup.tabPanel.items"
            :key="subIndex"
          >
            <template
              #header
              :title="t(subTabItem.label)"
            >
              {{ t(subTabItem.label) }}
            </template>
            <!-- Check if this subTabItem has its own tabPanel (nested tabPanels) -->
            <div
              v-if="subTabItem.type === 'tab-item' && subTabItem.tabPanel && subTabItem.tabPanel.items"
              class="tabs-field-group nested-tabs-field-group"
            >
              <TabView
                :key="`nested_tabs_${subIndex}`"
                :multiple="true"
                :scrollable="Object.prototype.hasOwnProperty.call(subTabItem, 'scrollable') && subTabItem.scrollable"
                @tab-change="(event) => handleNestedTabChange(event, subTabItem.tabPanel.items)"
              >
                <TabPanel
                  v-for="(nestedTabItem, nestedIndex) of subTabItem.tabPanel.items"
                  :key="`nested_tabs_${subIndex}_${nestedIndex}`"
                >
                  <template
                    #header
                    :title="t(nestedTabItem.label)"
                  >
                    {{ t(nestedTabItem.label) }}
                  </template>
                  <ConfigurationPanelTabContentFieldGroup
                    :field-group="nestedTabItem"
                    :tab-item="tabItem"
                    :parser-values="parserValues"
                    :is-synchronized-page="isSynchronizedPage"
                    :header-title="headerTitle"
                    :nested-tab-label="subTabItem.label"
                    :parent-field-group-label="fieldGroup.label"
                    @refresh="handleRefresh"
                  />
                </TabPanel>
              </TabView>
            </div>
            <!-- If no nested tabPanel, use the standard field group display -->
            <ConfigurationPanelTabContentFieldGroup
              v-else
              :field-group="subTabItem"
              :tab-item="tabItem"
              :parser-values="parserValues"
              :is-synchronized-page="isSynchronizedPage"
              :header-title="headerTitle"
              :parent-field-group-label="fieldGroup.label"
              @refresh="handleRefresh"
            />
          </TabPanel>
        </TabView>
      </div>
    </div>
    <div
      v-else-if="fieldGroup.type === 'notice'"
    >
      <NoticeBox
        v-if="fieldGroup.i18nKey && (!fieldGroup.condition || isNoticeConditionMet(fieldGroup.condition))"
        :severity="fieldGroup.severity || 'info'"
        :i18n-key="fieldGroup.i18nKey"
      />
    </div>
    <div
      v-else-if="fieldGroup.type === 'fields-group'"
    >
      <div
        v-if="fieldGroup.isNotCollapsible"
      >
        <span v-if="fieldGroup.label !== ''">{{ fieldGroupLabel }}</span>
      </div>
      <button
        v-else
        class="field-group-control field-group-control--collapsible flex justify-content-between align-items-center"
        @click="toggleFieldGroup(fieldGroupIndex)"
      >
        <span>{{ fieldGroupLabel }}</span>
        <i
          class="far"
          :class="activeFieldGroupIndex.includes(fieldGroupIndex) ? 'fa-chevron-up' : 'fa-chevron-down'"
        />
      </button>
      <ConfigurationPanelTabContentFieldGroup
        v-if="activeFieldGroupIndex.includes(fieldGroupIndex) || fieldGroup.isNotCollapsible"
        :field-group="fieldGroup"
        :tab-item="tabItem"
        :parser-values="parserValues"
        :is-synchronized-page="isSynchronizedPage"
        :header-title="headerTitle"
        @refresh="handleRefresh"
      />
    </div>
    <div
      v-else-if="fieldGroup.type === 'parent-fields'"
    >
      <div
        v-if="fieldGroup.isNotCollapsible"
      >
        <span v-if="fieldGroup.label !== ''">{{ fieldGroupLabel }}</span>
      </div>
      <button
        v-else
        class="field-group-control field-group-control--collapsible flex justify-content-between align-items-center"
        @click="toggleFieldGroup(fieldGroupIndex)"
      >
        <span>{{ fieldGroupLabel }}</span>
        <i
          class="far"
          :class="activeFieldGroupIndex.includes(fieldGroupIndex) ? 'fa-chevron-up' : 'fa-chevron-down'"
        />
      </button>
      <div
        v-if="activeFieldGroupIndex.includes(fieldGroupIndex) || fieldGroup.isNotCollapsible"
        class="parent-fields-content"
      >
        <ToggleMobileSettings
          v-if="tabItem.canToggleSettings && tabItem.canToggleSettings[fieldGroup.label]"
          class="mt-3 mb-3"
          :tab-item-label="tabItem.label"
          :parser-values="parserValues[tabItem.label]"
          :is-synchronized-page="isSynchronizedPage"
          :header-title="headerTitle"
          :field-group-label="fieldGroup.label"
          :tooltip-label="tabItem.canToggleSettings[fieldGroup.label].tooltipLabel"
          :label="tabItem.canToggleSettings[fieldGroup.label].label"
          @toggle-settings="handleChangeMobileSettings"
        />
        <div v-if="showFieldGroup">
          <div
            v-for="(group, indexGroup) of fieldGroup.fieldGroups"
            :key="`${fieldGroupIndex}-${indexGroup}`"
          >
            <div
              v-if="group.type === 'fields-group'"
            >
              <div class="mt-2">
                <span class="parent-fields-content-group-label">{{ t(group.label) }}</span>
              </div>
              <Divider class="parent-fields-content-divider" />
              <ConfigurationPanelTabContentFieldGroup
                :field-group="fieldGroup"
                :tab-item="tabItem"
                :parser-values="parserValues"
                :item-group="group"
                :is-synchronized-page="isSynchronizedPage"
                :header-title="headerTitle"
                @refresh="handleRefresh"
              />
            </div>
            <div
              v-else-if="group.type === 'tab-item'"
              class="tabs-field-group"
            >
              <div class="mt-2">
                <span class="parent-fields-content-group-label">{{ t(group.label) }}</span>
              </div>
              <Divider class="parent-fields-content-divider" />
              <div
                class="field-group-content field-group-content-tabbed"
              >
                <TabView
                  :key="`tabs_${group.id}`"
                  :multiple="true"
                  :scrollable="true"
                >
                  <TabPanel
                    v-for="(subTabItem, subIndex) of group.tabPanel.items"
                    :key="subIndex"
                  >
                    <template
                      #header
                      :title="t(subTabItem.label)"
                    >
                      {{ t(subTabItem.label) }}
                    </template>
                    <ConfigurationPanelTabContentFieldGroup
                      :field-group="subTabItem"
                      :tab-item="tabItem"
                      :parser-values="parserValues"
                      :sub-group-label="fieldGroup.label"
                      :is-synchronized-page="isSynchronizedPage"
                      :header-title="headerTitle"
                      @refresh="handleRefresh"
                    />
                  </TabPanel>
                </TabView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </li>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  computed,
  ref,
  onMounted,
  onUpdated,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { useStore } from '@/store';

import Tooltip from 'primevue/tooltip';

import SpmButton from '@/components/spm-primevue/SpmButton.vue';

import {
  FieldsGroup,
  TabItem,
  TabSubitem,
  ParserObject,
  SelectedStructureSyncPart,
  TemplateStructureEnum,
} from '@/types';
import StructureEdit from '@/components/template-builder/fields/StructureEdit.vue';
import ToggleMobileSettings from '@/components/template-builder/fields/partials/ToggleMobileSettings.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Divider from 'primevue/divider';
import { TemplateEditorState, showElementsModal, setEditorViewport } from '@/composables/template-editor/TemplateEditor';

import ConfigurationPanelTabContentFieldGroup from '@/components/template-builder/panels/ConfigurationPanelTabContentFieldGroup.vue';
import NoticeBox from '@/components/template-builder/panels/NoticeBox.vue';
import { useNoticeConditions } from '@/composables/template-editor/useShopifyPasswordNotice';
import { getTemplateIframeDocument } from '../utils/helpers';
import { HIDE_MOBILE_ATTRIBUTE } from '../utils/constants';

export default defineComponent({
  name: 'ConfigurationPanelTabContentItem',

  components: {
    StructureEdit,
    TabView,
    TabPanel,
    ConfigurationPanelTabContentFieldGroup,
    Divider,
    ToggleMobileSettings,
    SpmButton,
    HelpMessage,
    NoticeBox,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    tabItem: {
      type: Object as PropType<TabItem | TabSubitem>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserObject>,
      required: true,
    },

    fieldGroup: {
      type: Object as PropType<FieldsGroup | TabItem>,
      required: true,
    },

    fieldGroupIndex: {
      type: Number,
      required: true,
    },

    activeFieldGroupIndex: {
      type: Array as PropType<number[]>,
      required: true,
    },

    headerTitle: {
      type: String,
      required: false,
      default: '',
    },

    isSynchronizedPage: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['toggle-field-group', 'refresh'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    // Notice conditions for declarative `NOTICE` items.
    // Each panel item of type 'notice' may reference a condition by key;
    // here we read the computed boolean from the central registry.
    const noticeConditions = useNoticeConditions();
    const isNoticeConditionMet = (key: string): boolean => (
      noticeConditions[key]?.value === true
    );

    // Store active indices for sub-tabs and nested tabs to handle unmount callbacks
    const activeSubTabIndices: Record<string, number> = {};
    const activeNestedTabIndices: Record<string, number> = {};

    const handleSubTabChange = (event: { index: number }, tabItems: Array<FieldsGroup | TabItem>, fieldGroupId?: string) => {
      const key = fieldGroupId || 'default';
      const oldIndex = activeSubTabIndices[key];

      // Call onTabUnmount on previous tab if defined
      if (oldIndex !== undefined && oldIndex !== event.index) {
        const previousTab = tabItems[oldIndex];
        if (previousTab?.onTabUnmount) {
          previousTab.onTabUnmount();
        }
      }

      // Update active index
      activeSubTabIndices[key] = event.index;

      // Call onTabActivate on new tab if defined
      const currentTab = tabItems[event.index];
      if (currentTab?.onTabActivate) {
        currentTab.onTabActivate();
      }
    };

    const handleNestedTabChange = (event: { index: number }, nestedTabItems: Array<FieldsGroup | TabItem>, subTabId?: string) => {
      const key = subTabId || 'default';
      const oldIndex = activeNestedTabIndices[key];

      // Call onTabUnmount on previous tab if defined
      if (oldIndex !== undefined && oldIndex !== event.index) {
        const previousTab = nestedTabItems[oldIndex];
        if (previousTab?.onTabUnmount) {
          previousTab.onTabUnmount();
        }
      }

      // Update active index
      activeNestedTabIndices[key] = event.index;

      // Call onTabActivate on new tab if defined
      const currentTab = nestedTabItems[event.index];
      if (currentTab?.onTabActivate) {
        currentTab.onTabActivate();
      }
    };

    const mobileEditMode = computed(() => store.getters['liveEditor/getMobileEditMode']);

    const MOBILE_EDITABLE_LABELS = new Set([
      'templateBuilder.configs.widgetStyles',
      'templateBuilder.configs.column',
      'templateBuilder.configs.line',
      'templateBuilder.configs.gameWheelStyles',
      'templateBuilder.configs.gameSlotMachineStyles',
      'templateBuilder.configs.gameScratchCardStyles',
    ]);
    const isMobileEditableTabItem = computed(() => props.fieldGroup.type === 'tab-item' && MOBILE_EDITABLE_LABELS.has(props.fieldGroup.label));
    const isMobileStylesDisabled = computed(() => getTemplateIframeDocument()?.body.getAttribute(HIDE_MOBILE_ATTRIBUTE) === 'true');

    const switchToDesktop = () => {
      if (!mobileEditMode.value) return;
      setEditorViewport('desktop');
    };

    const switchToMobile = () => {
      if (mobileEditMode.value) return;
      setEditorViewport(TemplateEditorState.mobileDevice);
    };

    const showFieldGroup = computed(() => props.parserValues[props.tabItem.label].toggleItemsDisplay?.[props.fieldGroup.label]?.value ?? true);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);

    const componentInSyncPart = computed(() => {
      if ('elementPartType' in props.fieldGroup && props.fieldGroup.elementPartType && selectedStructure.value) {
        return selectedStructure.value.syncParts.find((syncPart: SelectedStructureSyncPart) => syncPart.partType === (props.fieldGroup as FieldsGroup).elementPartType);
      }
      return null;
    });

    const fieldGroupLabel = computed(() => {
      if (componentInSyncPart.value) {
        return `${t(props.fieldGroup.label)} : ${componentInSyncPart.value.partName}`;
      }
      return t(props.fieldGroup.label);
    });

    const selectedSyncElement = computed(() => store.getters['liveEditor/getSelectedSyncElement']);
    const hideLoadElementBtn = computed(() => {
      if (selectedSyncElement.value && props.fieldGroup.elementPartType && selectedStructure.value) {
        return selectedStructure.value.syncParts.find((syncPart: SelectedStructureSyncPart) => syncPart.syncId === selectedSyncElement.value.syncElementId);
      }
      return false;
    });

    const canSaveStyle = computed(() => {
      if (!selectedSyncElement.value) {
        return true;
      }
      if (selectedSyncElement.value && selectedSyncElement.value.elementType === TemplateStructureEnum.SECTION && !selectedSyncElement.value.elementPartType) {
        return true;
      }
      return false;
    });

    const shouldDisplayFieldGroup = computed(() => {
      if (!props.fieldGroup.isVisible) {
        return true;
      }

      return props.fieldGroup.isVisible();
    });

    const toggleFieldGroup = (index: number) => {
      emit('toggle-field-group', index);
    };

    const handleRefresh = () => {
      emit('refresh');
    };

    const handleChangeMobileSettings = (data: { tabItemLabel: string; fieldGroupLabel: string; value: boolean }) => {
      store.dispatch('liveEditor/toggleSetting', data);
    };

    const handleShowSaveElementModal = () => {
      store.commit('liveEditor/showSaveElementModal', {
        selector: selectedStructure.value ? `#${selectedStructure.value.identifier}` : '',
        type: TemplateStructureEnum.WIDGET,
        elementPartType: props.fieldGroup.elementPartType,
        fromSyncElement: !canSaveStyle.value,
      });
    };

    const handleShowElementModal = () => {
      if (selectedStructure.value) {
        const template = getTemplateIframeDocument();
        if (template) {
          const element = template.querySelector(`#${selectedStructure.value.identifier}`);
          const elementSubtype = element?.getAttribute('data-widgettype') || '';
          showElementsModal([selectedStructure.value.type], {
            elementPartType: props.fieldGroup.elementPartType || '',
            elementSubtype,
            fromSyncElement: !!selectedSyncElement.value,
          });
        }
      }
    };

    const enterEditSyncElement = () => {
      const elementType = componentInSyncPart.value ? selectedStructure.value.type : TemplateStructureEnum.DESIGN;
      const name = componentInSyncPart.value ? '' : props.headerTitle;

      store.dispatch('liveEditor/showSyncElementWarning', {
        elementId: selectedStructure.value.identifier,
        elementType,
        name,
        elementPartType: props.fieldGroup.elementPartType,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        acceptCallback: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        rejectCallback: () => {},
      });
    };

    // Ref for TabView to force scrollable arrows recalculation
    const tabViewRef = ref<any>(null);
    const updateTimeout = ref<any>(null);

    // Update button visibility based on scroll position
    const updateButtonVisibility = () => {
      if (!tabViewRef.value) return;

      const tabViewEl = tabViewRef.value.$el;
      if (!tabViewEl) return;

      const navContent = tabViewEl.querySelector('.p-tabview-nav-content') as HTMLElement | null;
      const prevBtn = tabViewEl.querySelector('.p-tabview-nav-prev') as HTMLElement | null;
      const nextBtn = tabViewEl.querySelector('.p-tabview-nav-next') as HTMLElement | null;

      if (navContent) {
        const { scrollLeft, scrollWidth, clientWidth } = navContent;
        const canScrollLeft = scrollLeft > 0;
        const canScrollRight = Math.ceil(scrollLeft + clientWidth) < scrollWidth;

        // Hide/show prev button based on scroll position
        if (prevBtn) {
          prevBtn.style.display = canScrollLeft ? 'flex' : 'none';
        }

        // Hide/show next button based on scroll position
        if (nextBtn) {
          nextBtn.style.display = canScrollRight ? 'flex' : 'none';
        }
      }
    };

    // Force TabView scrollable arrows recalculation (fixes PrimeVue bug)
    const updateTabViewScrollButtons = () => {
      nextTick(() => {
        updateTimeout.value = setTimeout(() => {
          if (tabViewRef.value) {
            // Try to call updateButtonState if available (PrimeVue internal method)
            if (typeof tabViewRef.value.updateButtonState === 'function') {
              tabViewRef.value.updateButtonState();
            }
            // Access the DOM element and trigger scroll to force button state update
            const tabViewEl = tabViewRef.value.$el;
            if (tabViewEl) {
              const navContent = tabViewEl.querySelector('.p-tabview-nav-content');
              if (navContent) {
                // Trigger a small scroll to force PrimeVue to recalculate
                navContent.scrollLeft = 1;
                navContent.scrollLeft = 0;

                // Add scroll event listener to update button visibility on scroll
                navContent.removeEventListener('scroll', updateButtonVisibility);
                navContent.addEventListener('scroll', updateButtonVisibility);
              }
            }
            // Also trigger resize event as fallback
            window.dispatchEvent(new Event('resize'));

            // Update button visibility based on current scroll position
            updateButtonVisibility();
          }
        }, 100);
      });
    };

    onMounted(() => {
      updateTabViewScrollButtons();
      setTimeout(updateTabViewScrollButtons, 300);
    });

    onUpdated(() => {
      updateTabViewScrollButtons();
    });

    onBeforeUnmount(() => {
      // Check if fieldGroup has tabPanel with items
      if (props.fieldGroup.tabPanel?.items) {
        // Call onTabUnmount for active sub-tabs
        Object.keys(activeSubTabIndices).forEach((key) => {
          const activeIndex = activeSubTabIndices[key];
          const activeTab = props.fieldGroup.tabPanel!.items[activeIndex];
          if (activeTab?.onTabUnmount) {
            activeTab.onTabUnmount();
          }
        });

        // Call onTabUnmount for active nested tabs
        Object.keys(activeNestedTabIndices).forEach((key) => {
          const activeIndex = activeNestedTabIndices[key];
          // Find the nested tab in the structure
          props.fieldGroup.tabPanel!.items.forEach((subTab) => {
            if (subTab.tabPanel?.items) {
              const nestedTab = subTab.tabPanel.items[activeIndex];
              if (nestedTab?.onTabUnmount) {
                nestedTab.onTabUnmount();
              }
            }
          });
        });
      }

      if (updateTimeout.value) {
        clearTimeout(updateTimeout.value);
      }
    });

    return {
      t,
      showFieldGroup,
      fieldGroupLabel,
      hideLoadElementBtn,
      componentInSyncPart,
      selectedSyncElement,
      canSaveStyle,
      shouldDisplayFieldGroup,
      toggleFieldGroup,
      handleRefresh,
      handleChangeMobileSettings,
      handleShowSaveElementModal,
      handleShowElementModal,
      enterEditSyncElement,
      handleSubTabChange,
      handleNestedTabChange,
      isNoticeConditionMet,
      tabViewRef,
      mobileEditMode,
      isMobileEditableTabItem,
      isMobileStylesDisabled,
      switchToDesktop,
      switchToMobile,
    };
  },
});
</script>

<style lang="scss" scoped>
.field-group:not(:last-child) {
  margin-bottom: .5rem;
}

.nested-tabs-field-group {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.configuration-wrapper {
  & .p-panel {
    & > .p-toggleable-content {
      & .p-panel-content {
        & .field-group {
            & .field-group-control {
              text-align: left;
              background-color: $shuttle-light-grey !important;

              &--collapsible:deep() {
                width: 100%;
                border: 0 none;
                cursor: pointer;
                background-color: #fbfbfb !important;
                border-bottom: 1px solid #e9ecef;
              }
            }
          }
      }
    }
  }

  .tabs-field-group:deep() {
    & .p-tabview {
      margin: 5px 0;

      & .p-tabview-nav-container {
        position: relative;
        overflow: hidden;
      }

      & .p-tabview-nav-content {
        overflow-x: auto;
        overflow-y: hidden;
        scroll-behavior: smooth;
        scrollbar-width: none; // Firefox
        -ms-overflow-style: none; // IE/Edge

        &::-webkit-scrollbar {
          display: none; // Chrome/Safari
        }
      }

      // Style navigation buttons when they appear
      & .p-tabview-nav-btn {
        position: absolute;
        top: 0;
        z-index: 2;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #ffffff;
        border: none;
        cursor: pointer;
        padding: 0 0.5rem;

        &.p-tabview-nav-prev {
          left: 0;
        }

        &.p-tabview-nav-next {
          right: 0;
        }
      }

      & .p-tabview-nav {
        font-size: 0.875rem;
        line-height: 1.0625rem;
        white-space: nowrap;

        & li {
          flex-grow: unset !important;

          & .p-tabview-nav-link {
            text-transform: none;
            font-weight: bold !important;
            color: $tuna !important;
            padding: 12px;
          }
        }
      }
    }

    & .p-tabview-panels {
      padding: 0;

      & .field-group-content {
        padding: 0;
      }
    }
  }
}

.mobile-edit-switch {
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;

  &__toggle {
    display: flex;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    overflow: hidden;
  }

  &__btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    padding: 0.35rem 0.5rem;
    border: none;
    background: #fff;
    color: #6c757d;
    font-size: 0.78rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover:not(&--active) {
      background: #f8f9fa;
    }

    &--active {
      background: #495057;
      color: #fff;
      cursor: default;
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    i {
      font-size: 0.8rem;
    }
  }

  &__indicator {
    margin-top: 0.4rem;
    padding: 0.3rem 0.6rem;
    background: #fff3e0;
    border: 1px solid #ffb74d;
    border-radius: 4px;
    color: #e65100;
    font-size: 0.72rem;
    font-weight: 500;
  }
}

.parent-fields-content {
  padding: 0 1.5rem;
}
.parent-fields-content-divider {
  border-top: 1px solid #dee2e6;
  margin: 0.5rem 0 !important;
}
.parent-fields-content-group-label {
  color: #6c757d;
  font-size: 0.95rem;
}
</style>
